import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClientDTO, RoleRequestDTO, RoleUserDTO } from '../../../core/models/role.model';
import { RoleService } from '../../../core/services/role.service';

import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';

export interface RoleFormDialogData {
  mode: 'create' | 'edit';
  role?: RoleUserDTO;
  clientId?: string;
  isRealm?: boolean;
}

@Component({
  selector: 'app-role-form-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatButtonModule
],
  templateUrl: './role-form-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./role-form-dialog.component.scss']
})
export class RoleFormDialogComponent implements OnInit, OnDestroy {
  roleForm!: FormGroup;
  clients: ClientDTO[] = [];
  isEditMode = false;
  isRealmRole = false;
  loading = false;
  submitting = false;
  originalRoleName = '';
  clientId = '';

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<RoleFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RoleFormDialogData,
    private fb: FormBuilder,
    private roleService: RoleService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.isEditMode = this.data.mode === 'edit';
    this.clientId = this.data.clientId || '';
    this.isRealmRole = this.data.isRealm ?? false;

    this.initForm();
    this.loadClients();

    if (this.isEditMode && this.data.role) {
      this.loadRoleData(this.data.role);
    //   console.log("RÔLE : ", this.loadRoleData(this.data.role));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.roleForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50), Validators.pattern(/^[a-zA-Z0-9_-]+$/)]],
      description: ['', [Validators.maxLength(255)]],
      title: ['', [Validators.maxLength(100)]],
      roleType: [this.isRealmRole ? 'realm' : 'client', Validators.required],
      clientId: [this.clientId || '', this.isRealmRole ? [] : [Validators.required]],
      composite: [false]
    });

    // Désactiver les champs en édition
    if (this.isEditMode) {
      this.roleForm.get('name')?.disable();
      this.roleForm.get('roleType')?.disable();
      this.roleForm.get('clientId')?.disable();
    }

    // Réactivité du type de rôle
    this.roleForm.get('roleType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        this.isRealmRole = type === 'realm';
        const clientControl = this.roleForm.get('clientId');
        if (this.isRealmRole) {
          clientControl?.clearValidators();
          clientControl?.setValue('');
        } else {
          clientControl?.setValidators(Validators.required);
        }
        clientControl?.updateValueAndValidity();
      });
  }

  private loadClients(): void {
    this.roleService.getClients()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.clients = response.content.filter(c => c.enabled);
        },
        error: (error) => {
          console.error('Erreur chargement clients:', error);
          this.showError('Erreur lors du chargement des clients');
        }
      });
  }

  private loadRoleData(role: RoleUserDTO): void {
    this.originalRoleName = role.name;
    this.roleForm.patchValue({
      name: role.name,
      description: role.description || '',
      title: role.title || '',
      roleType: role.clientRole ? 'client' : 'realm',
      clientId: this.clientId || '',
      composite: role.composite
    });
  }

  onSubmit(): void {
    if (this.roleForm.invalid) {
      this.markFormGroupTouched(this.roleForm);
      this.showError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    this.submitting = true;
    const formValue = this.roleForm.getRawValue();
    const roleRequest: RoleRequestDTO = {
      name: formValue.name,
      description: formValue.description || undefined,
      composite: formValue.composite,
      title: formValue.title || undefined
    };

    if (this.isEditMode) {
      this.updateRole(roleRequest);
    } else {
      this.createRole(roleRequest, formValue.roleType, formValue.clientId);
    }
  }

  private createRole(roleRequest: RoleRequestDTO, roleType: string, clientId: string): void {
    this.roleService.roleExists(roleRequest.name, roleType === 'client' ? clientId : undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (exists) => {
          if (exists) {
            this.showError(`Un rôle avec le nom "${roleRequest.name}" existe déjà`);
            this.submitting = false;
            return;
          }
          const createObs = roleType === 'realm'
            ? this.roleService.createRealmRole(roleRequest)
            : this.roleService.createClientRole(clientId, roleRequest);
          createObs.pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.showSuccess(`Rôle "${roleRequest.name}" créé avec succès`);
                this.dialogRef.close(true);
              },
              error: (error) => {
                this.handleError(error);
                this.submitting = false;
              }
            });
        },
        error: (error) => {
          console.error('Erreur vérification:', error);
          this.submitting = false;
        }
      });
  }

  private updateRole(roleRequest: RoleRequestDTO): void {
    const updateObs = this.isRealmRole
      ? this.roleService.updateRealmRole(this.originalRoleName, roleRequest)
      : this.roleService.updateClientRole(this.clientId, this.originalRoleName, roleRequest);
    updateObs.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showSuccess('Rôle mis à jour avec succès');
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.handleError(error);
          this.submitting = false;
        }
      });
  }

  private handleError(error: any): void {
    if (error.status === 409) this.showError('Un rôle avec ce nom existe déjà');
    else if (error.status === 400) this.showError('Données invalides. Vérifiez le formulaire');
    else if (error.error?.message) this.showError(error.error.message);
    else this.showError('Une erreur est survenue. Veuillez réessayer');
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) this.markFormGroupTouched(control);
    });
  }

  getErrorMessage(fieldName: string): string {
    const control = this.roleForm.get(fieldName);
    if (!control || !control.touched) return '';
    if (control.hasError('required')) return 'Ce champ est requis';
    if (control.hasError('minlength')) {
      const min = control.errors?.['minlength'].requiredLength;
      return `Minimum ${min} caractères`;
    }
    if (control.hasError('maxlength')) {
      const max = control.errors?.['maxlength'].requiredLength;
      return `Maximum ${max} caractères`;
    }
    if (control.hasError('pattern')) {
      return 'Seuls les lettres, chiffres, tirets et underscores sont autorisés';
    }
    return '';
  }

  hasError(fieldName: string): boolean {
    const control = this.roleForm.get(fieldName);
    return !!(control && control.invalid && control.touched);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Fermer', { duration: 3000, panelClass: ['success-snackbar'], horizontalPosition: 'end', verticalPosition: 'top' });
  }
  private showError(message: string): void {
    this.snackBar.open(message, 'Fermer', { duration: 5000, panelClass: ['error-snackbar'], horizontalPosition: 'end', verticalPosition: 'top' });
  }
}